import clsx from 'clsx';
import { CloudOff, Cpu, Laptop, Lock, ShieldCheck } from 'lucide-react';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const REQUEST = 'Reply to HR confirming my medical leave starting March 3.';
const RESPONSE =
  'Hi Dana, confirming my medical leave starts March 3. I’ll send the paperwork today.';

// Five tracks on desktop (step, arrow, step, arrow, step); one column of five rows on phones.
const GRID_CLASS_NAME =
  'relative grid grid-cols-1 grid-rows-[auto_48px_auto_48px_auto] pt-[30px] md:grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)_72px_minmax(0,1fr)] md:grid-rows-1';
const PLACEMENT = [
  'col-start-1 row-start-1',
  'col-start-1 row-start-2 md:col-start-2 md:row-start-1',
  'col-start-1 row-start-3 md:col-start-3 md:row-start-1',
  'col-start-1 row-start-4 md:col-start-4 md:row-start-1',
  'col-start-1 row-start-5 md:col-start-5 md:row-start-1',
] as const;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/** The same request run on-device and in Private Cloud, step by step. */
const PrivateAiFlow: React.FC = () => (
  <div className="flex flex-col gap-10" aria-hidden>
    <FlowRow
      title="On-device"
      note={
        <>
          <CloudOff className="size-3.5" />
          No network request
        </>
      }
    >
      <DeviceBox className="col-start-1 row-start-1 row-end-6 md:col-end-6 md:row-end-2" />
      <MessageStep step={1} label="Request" text={REQUEST} />
      <Arrow leg={1} />
      <ModelStep icon={<Cpu />} title="On-device model" detail="Runs on your device" />
      <Arrow leg={2} />
      <MessageStep step={3} label="Response" text={RESPONSE} />
    </FlowRow>
    <FlowRow
      title="Private Cloud"
      note={
        <>
          <Lock className="size-3.5" />
          Encrypted end to end
        </>
      }
    >
      <DeviceBox className="col-start-1 row-start-1" />
      <DeviceBox className="col-start-1 row-start-5 md:col-start-5 md:row-start-1" />
      <MessageStep step={1} label="Request" text={REQUEST} />
      <Arrow leg={1} ciphertext="7f3a 9c0e" />
      <ModelStep
        icon={<ShieldCheck />}
        title="Enclave (TEE)"
        detail="The only place it’s decrypted"
        enclave
      />
      <Arrow leg={2} ciphertext="b81c 04fa" />
      <MessageStep step={3} label="Response" text={RESPONSE} />
    </FlowRow>
  </div>
);

const FlowRow: React.FC<{ title: string; note: React.ReactNode; children: React.ReactNode }> = ({
  title,
  note,
  children,
}) => (
  <div>
    <div className="mb-3 flex items-baseline justify-between gap-3 text-[13px] leading-5 font-medium">
      {title}
      <span className="text-gray-11 inline-flex items-center gap-1.5 text-xs font-normal">
        {note}
      </span>
    </div>
    <div className={GRID_CLASS_NAME}>{children}</div>
  </div>
);

/** The device boundary drawn behind the steps that happen on it. */
const DeviceBox: React.FC<{ className: string }> = ({ className }) => (
  <div
    className={clsx(
      'border-gray-6 bg-gray-3 relative -mx-2.5 -mt-[30px] -mb-2.5 rounded-xl border',
      className
    )}
  >
    <span className="text-gray-11 absolute top-[7px] left-2.5 flex items-center gap-1.5 text-xs">
      <Laptop className="size-3.5" />
      Your device
    </span>
  </div>
);

const MessageStep: React.FC<{ step: 1 | 3; label: string; text: string }> = ({
  step,
  label,
  text,
}) => (
  <div
    data-step={step}
    className={clsx(
      'landing-flow-step border-gray-6 bg-gray-1 relative z-10 flex min-h-24 flex-col gap-1 rounded-lg border px-3 py-2.5',
      PLACEMENT[step === 1 ? 0 : 4]
    )}
  >
    <span className="text-gray-11 text-[11px] leading-4">{label}</span>
    <p className={clsx('text-xs leading-[18px]', step === 3 && 'landing-flow-response')}>{text}</p>
  </div>
);

const ModelStep: React.FC<{
  icon: React.ReactNode;
  title: string;
  detail: string;
  enclave?: boolean;
}> = ({ icon, title, detail, enclave = false }) => (
  <div
    data-step={2}
    className={clsx(
      'landing-flow-step relative z-10 flex min-h-24 flex-col justify-center gap-1 rounded-lg border px-3 py-2.5 [&_svg]:size-3.5',
      enclave
        ? 'border-blue-7 bg-blue-3 text-blue-11 border-dashed'
        : 'border-gray-6 bg-gray-1 [&_svg]:text-gray-11',
      PLACEMENT[2]
    )}
  >
    <strong className="flex items-center gap-1.5 text-[13px] leading-5 font-medium">
      {icon}
      {title}
    </strong>
    <span className={clsx('text-[11px] leading-4', !enclave && 'text-gray-11')}>{detail}</span>
  </div>
);

const Arrow: React.FC<{ leg: 1 | 2; ciphertext?: string }> = ({ leg, ciphertext }) => (
  <div
    data-encrypted={ciphertext ? '' : undefined}
    className={clsx(
      'landing-flow-arrow relative z-10 self-stretch md:h-10 md:self-center',
      PLACEMENT[leg === 1 ? 1 : 3]
    )}
  >
    {ciphertext ? (
      <>
        <em className="text-gray-11 absolute top-2.5 left-[calc(50%+10px)] text-[10px] leading-3.5 not-italic md:inset-x-0 md:-top-0.5 md:text-center">
          Encrypted
        </em>
        <code className="text-gray-9 absolute top-6 left-[calc(50%+10px)] overflow-hidden font-mono text-[10px] leading-3.5 whitespace-nowrap md:inset-x-0 md:top-[26px] md:text-center">
          {ciphertext}
        </code>
        <span
          data-leg={leg}
          className="landing-flow-packet bg-blue-9 absolute top-[11px] flex size-[18px] items-center justify-center rounded-[5px] text-white max-md:hidden"
        >
          <Lock className="size-[11px]" />
        </span>
      </>
    ) : (
      <span
        data-leg={leg}
        className="landing-flow-packet bg-blue-9 absolute top-4 size-2 rounded-full max-md:hidden"
      />
    )}
  </div>
);

export default PrivateAiFlow;
